import { Link } from 'react-router-dom';
import CallSplitOutlinedIcon from '@mui/icons-material/CallSplitOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { PageHero } from '@/components/PageHero';
import { Consolidation } from '@/components/Consolidation';
import { NotifyForm } from '@/components/NotifyForm';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/seo/useSeo';

type Seam = { icon: SvgIconComponent; title: string; body: string };

const seams: Seam[] = [
  {
    icon: WarningAmberOutlinedIcon,
    title: 'An alert becomes a ticket only if the integration cooperates',
    body: 'An alert fires in the RMM at 2am. It becomes a ticket in the PSA — if the integration is configured right, if it doesn\'t silently fail, and if the two systems agree on which device and which client the alert belongs to.',
  },
  {
    icon: CallSplitOutlinedIcon,
    title: 'Techs tab-switch to see what the other tool already knows',
    body: 'A tech resolving a ticket has to open a second tool to see the device\'s monitoring history, because the PSA doesn\'t know what the RMM knows — and vice versa.',
  },
  {
    icon: SyncAltOutlinedIcon,
    title: 'SLA reporting reconciles two clocks that were never meant to agree',
    body: 'SLA timestamps come from two systems that don\'t share a clock, which means the report a client sees at a QBR is an approximation stitched together after the fact, not a fact.',
  },
  {
    icon: TuneOutlinedIcon,
    title: 'The glue is "just glue" until it breaks',
    body: 'Every integration point between two vendors is a new thing that can silently fail — and because it\'s "just glue," it often doesn\'t get monitored as carefully as the core tools it connects.',
  },
];

export function SolutionsConsolidation(): JSX.Element {
  useSeo({
    title: 'IT consolidation — PSA and RMM in one platform',
    description:
      'Nexus replaces the PSA, RMM, backup, security, and CRM stack MSPs stitch together with one tenant-isolated platform — removing the seams between tools, not just adding another one.',
    path: '/solutions/it-consolidation',
  });

  return (
    <div>
      <PageHero
        eyebrow="IT consolidation"
        title="Every seam between two tools is a place time or accuracy quietly leaks out."
        subtitle="Nexus isn't a slightly better PSA next to a slightly better RMM. It's one tenant-isolated platform where a ticket, a device, and an SLA clock are the same record — because there's no second system for them to cross a boundary from."
      >
        <Reveal delay={150}>
          <div className="mt-9 flex justify-center">
            <NotifyForm />
          </div>
        </Reveal>
      </PageHero>

      <section className="py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="mx-auto mb-14 max-w-2xl text-center">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-nexus-purple-light">
                Where the seams actually cost you
              </span>
              <h2 className="mt-3 font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                &quot;Best of breed&quot; has a hidden line item.
              </h2>
              <p className="mt-4 text-white/70">
                The pitch for running a separate PSA and RMM is always picking the best tool for
                each job independently. In practice, the seams between them are where the real
                cost hides.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {seams.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={i * 90}>
                  <div className="h-full rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
                      <Icon fontSize="small" />
                    </div>
                    <h3 className="font-display text-[17px] font-semibold text-nexus-ink">{s.title}</h3>
                    <p className="mt-1.5 text-sm text-white/65">{s.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <Consolidation />

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content max-w-[760px]">
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">
              Consolidation is only a win if both halves are actually good
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-white/75">
              A combined platform that&apos;s mediocre at both ticketing and monitoring is worse than
              two great specialized tools. The bar we hold ourselves to: the combined platform has to
              be as good at ticketing as a dedicated PSA and as good at monitoring as a dedicated RMM,
              with the seam-cost removed as pure upside on top — not a trade-off you accept in exchange
              for one bill.
            </p>
            <p className="mt-4 text-[15px] leading-relaxed text-white/75">
              We don&apos;t ask you to take that on faith either. Every module on the{' '}
              <Link to="/platform" className="text-nexus-purple-light no-underline hover:underline">
                platform page
              </Link>{' '}
              carries an honest maturity label, and the{' '}
              <Link to="/roadmap" className="text-nexus-purple-light no-underline hover:underline">
                roadmap
              </Link>{' '}
              tracks exactly what&apos;s shipped versus still building.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
              <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                Ready to remove the seams?
              </h2>
              <p className="mx-auto mt-3 max-w-[520px] text-white/70">
                Nexus is live in our own MSP operations and opening to a limited design-partner
                cohort. Join the private-preview list.
              </p>
              <div className="mt-8 flex justify-center">
                <NotifyForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
